import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { IQuery, UserCreateDto } from "@eagerdog/interfaces";
import { Constants } from "@eagerdog/constants";

import { apiService } from 'src/services';
import { helperService } from "src/services/helper.service";
import { useDebounce } from "src/hooks/useDebounce";

import { toast } from '../Toast/ToastManager';
import Input from "../Input/Input";
import Phone from "../Phone/Phone";
import Button from "../Button/Button";
import Checkbox from "../Checkbox/Checkbox";

import styles from "./UserSearch.module.scss";

interface IProps {
  event?: string,
  course?: string
}

const UserSearch: React.FC<IProps> = (props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const [emailSearch, setEmailSearch] = useState<string>("");
  const dEmailSearch = useDebounce<string>(emailSearch, 800);

  const [userList, setUserList] = useState<[]>([]);

  const [loading, setLoading] = useState<boolean>(false);
  const [createLoading, isCreateLoading] = useState<boolean>(false);

  const [createNewUser, setCreateNewUser] = useState<boolean>(false);

  const [email, setEmail] = useState<string>("");
  const [firstName, setFirstName] = useState<string>("");
  const [lastName, setLastName] = useState<string>("");
  const [phone, setPhone] = useState<string>("");

  const [isJunior, setIsJunior] = useState<boolean>(false);

  const [akcJuniorNum, setAkcJuniorNum] = useState<string>("");
  const [ukcJuniorNum, setUkcJuniorNum] = useState<string>("");
  const [ckcJuniorNum, setCkcJuniorNum] = useState<string>("");
  const [otherJuniorNum, setOtherJuniorNum] = useState<string>("");
  const [parentName, setParentName] = useState<string>("");
  const [juniorDob, setJuniorDob] = useState<string>("");

  // Get admin context for registration URL
  const getAdminContext = () => {
    const currentUser = helperService.getUser();
    
    // Check if we're in admin dashboard context
    const isAdminContext = location.pathname.startsWith('/admin/clubs/');
    const adminParam = searchParams.get('admin');
    
    if (isAdminContext || adminParam) {
      return adminParam || 'admin'; // Use the admin param or default to 'admin'
    }
    
    // Check if current user has admin capacity for this event's club
    // If they're an owner (not club_manager) accessing this modal, they must have admin capacity
    if (currentUser.user_type === Constants.user_type.owner && props.event) {
      return 'true';
    }
    
    return null;
  };

  const searchUsers = (searchTerm: string) => {
    setLoading(true);

    let query:IQuery = {
      $limit: 3,
      sort: [{
        attribute_name: "email",
        sort: "desc"
      }],
      query: {
        $and: [
          {
            $or: [
              {
                attribute_name: "email",
                attribute_value: {
                  operator: "$regex",
                  value: searchTerm
                }
              },
              {
                attribute_name: "display_name",
                attribute_value: {
                  operator: "$regex",
                  value: searchTerm
                }
              },
              {
                attribute_name: "first_name",
                attribute_value: {
                  operator: "$regex",
                  value: searchTerm
                }
              },
              {
                attribute_name: "last_name",
                attribute_value: {
                  operator: "$regex",
                  value: searchTerm
                }
              }
            ]
          },
          {
            attribute_name: "user_type",
            attribute_value: {
              operator: "$eq",
              value: "owner"
            }
          }
        ]
      }
    };

    apiService.getUsers(query).then((response: any) => {
      setUserList(response);
    }).finally(() => {
      setLoading(false);
    });
  }

  const createUser = () => {
    isCreateLoading(true);

    let payload:UserCreateDto = {
      email: email,
      first_name: firstName,
      last_name: lastName,
      display_name: firstName + " " + lastName,
      phone: phone
    };

    if (isJunior) {
      payload.is_junior = isJunior;
      payload.junior_numbers = [];
      payload.parent_name = parentName;
      if (juniorDob) {
        payload.junior_dob = new Date(juniorDob);
      }

      if (akcJuniorNum.length > 0) {
        payload.junior_numbers.push({ name: Constants.sanctioning_club.AKC, value: akcJuniorNum });
      }

      if (ukcJuniorNum.length > 0) {
        payload.junior_numbers.push({ name: Constants.sanctioning_club.UKC, value: ukcJuniorNum });
      }

      if (ckcJuniorNum.length > 0) {
        payload.junior_numbers.push({ name: Constants.sanctioning_club.CKC, value: ckcJuniorNum });
      }

      if (otherJuniorNum.length > 0) {
        payload.junior_numbers.push({ name: Constants.sanctioning_club.other, value: otherJuniorNum });
      }
    }

    apiService.createUserByClubManager(payload).then((response) => {
      toast.show({
        title: "Create User",
        content: "User created successfully",
        duration: 10000,
        type: "success"
      });

      const adminContext = getAdminContext();
      const adminParam = adminContext ? `&admin=${adminContext}` : '';
      const userName = `${firstName} ${lastName}`.trim();
      
      if (props.event) {
        navigate("/e/" + props.event + "/register?uid=" + response._id + "&uemail=" + encodeURIComponent(response.email) + "&uname=" + encodeURIComponent(userName) + "&junior="+ (isJunior ? "true" : "false") + "&claimed=false" + adminParam);
      } else {
        navigate("/c/" + props.course + "/register?uid=" + response._id + "&uemail=" + encodeURIComponent(response.email) + "&uname=" + encodeURIComponent(userName) + "&junior="+ (isJunior ? "true" : "false") + "&claimed=false" + adminParam);
      }
    }).catch((e: any) => {
      toast.show({
        title: "Create User",
        content: e.response?.data?.message ? e.response?.data?.message : "Unable to Create User",
        duration: 10000,
        errorDetails: e,
        type: "fail"
      });
    }).finally(() => {
      isCreateLoading(false);
    });
  }

  const pdEmailSearch = helperService.usePrevious(dEmailSearch);

  useEffect(() => {
    if (emailSearch.length > 0 && dEmailSearch !== pdEmailSearch) {
      searchUsers(emailSearch);
    }
  }, [emailSearch, dEmailSearch, pdEmailSearch]);

  return(
    <div className={styles.UserSearch}>
      {!createNewUser && <div className={styles.userSearchInner}>
        <div className={styles.title}>User Search</div>
        <div className={styles.description}>To register a user for your event, search for them by email or display name.</div>
        <form className={styles.searchUsers} onSubmit={((e) => { setEmail(emailSearch); setCreateNewUser(true); })}>
          <Input autoComplete="off" type="email" onChange={(e) => { setEmailSearch(e.target.value); }} defaultValue={emailSearch} placeholder="users@email.com" />
          <Button className={styles.Button} disabled={loading} type="submit">Create New User</Button>
        </form>
        {!loading && emailSearch.length > 0 && userList.length === 0 && <div className={styles.noUserFound}>
          <span className={styles.msg}>Account Not Found</span>
        </div>}
        {!loading && emailSearch.length > 0 && userList.length > 0 && <ul className={styles.usersFound}>
          {userList.map((user: any, index: number) => {
            const adminContext = getAdminContext();
            const userName = user.display_name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email;
            let baseUrl = "/e/" + props.event + "/register?uid=" + user._id + "&uemail=" + encodeURIComponent(user.email) + "&uname=" + encodeURIComponent(userName) + "&junior="+ (user.is_junior ? "true" : "false") +"&claimed="+(user.auth_type === "password" ? "true" : "false");
            
            if (props.course) {
              baseUrl = "/c/" + props.course + "/register?uid=" + user._id + "&uemail=" + encodeURIComponent(user.email) + "&uname=" + encodeURIComponent(userName) + "&junior="+ (user.is_junior ? "true" : "false") +"&claimed="+(user.auth_type === "password" ? "true" : "false");
            }
            
            const registrationUrl = adminContext ? baseUrl + "&admin=" + adminContext : baseUrl;
            
            return(
              <li key={index}>
                <Link to={registrationUrl}>
                  <div className={styles.userInfo}>
                    <div className={styles.userName}>{user.first_name} {user.last_name}</div>
                    <div className={styles.userEmail}>{user.email}</div>
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>}
        {loading && <div className={styles.loadWrap}>
          <div id={styles.loading}></div>
        </div>}
      </div>}
      {createNewUser && <div className={styles.createNewUser}>
        <div className={styles.title}>Create User</div>
        <form onSubmit={(e) => { e.preventDefault(); createUser(); }}>
          <div className={styles.row}>
            <Input required label={isJunior ? "Parent Email" : "Email"} type="email" onChange={(e) => { setEmail(e.target.value); }} placeholder="" defaultValue={email} />
          </div>
          <div className={`${styles.row} ${styles.double}`}>
            <Input required label={"First Name"} type="text" onChange={(e) => { setFirstName(e.target.value); }} placeholder="" />
            <Input required label={"Last Name"} type="text" onChange={(e) => { setLastName(e.target.value); }} placeholder="" />
          </div>
          <div className={styles.row}>
            <Phone required onChange={setPhone} value={phone} />
          </div>
          <div className={`${styles.row} ${styles.junior}`}>
            <Checkbox onChange={(e) => { setIsJunior(!isJunior); }} value={isJunior} id="isJunior" label="This is a junior account" />
            {isJunior && <>
              <Input required onChange={(e) => { setParentName(e.target.value); }} type="parentName" label="Parent Name" placeholder="What's the parents name?" />
              <Input required onChange={(e) => { setJuniorDob(e.target.value); }} type="date" label="Date of Birth" placeholder="MM/DD/YYYY" />
              <Input label="AKC Junior # (optional)" id="akcJuniorNum" type="text" onChange={(e) => { e.stopPropagation(); setAkcJuniorNum(e.target.value); }} defaultValue={akcJuniorNum} placeholder="" />
              <Input label="UKC Junior # (optional)" id="ukcJuniorNum" type="text" onChange={(e) => { e.stopPropagation(); setUkcJuniorNum(e.target.value); }} defaultValue={ukcJuniorNum} placeholder="" />
              <Input label="CKC Junior # (optional)" id="ckcJuniorNum" type="text" onChange={(e) => { e.stopPropagation(); setCkcJuniorNum(e.target.value); }} defaultValue={ckcJuniorNum} placeholder="" />
              <Input label="Other Junior # / Identifier" id="otherJuniorNum" type="text" onChange={(e) => { e.stopPropagation(); setOtherJuniorNum(e.target.value); }} defaultValue={otherJuniorNum} placeholder="" />
            </>}
          </div>
          <div className={styles.actions}>
            <Button onClick={() => { setCreateNewUser(false); }} type="button">Back</Button>
            <Button isLoading={createLoading} type="submit">Create</Button>
          </div>
        </form>
      </div>}
    </div>
  );
}

export default UserSearch;